// src/components/OnboardingCallout.js
// Dismissible onboarding callout — shown once per user per key.
// Dismissal is stored in users.dismissed_callouts, NOT localStorage. This comment
// said localStorage until Session 78h; it has not been true since #7/#26 moved the
// store to the database, and it was stale on the file every new placement copies from.
// Desktop: tooltip anchored near a target element (above or below, auto-flips).
// Mobile: bottom-anchored banner strip.

import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { hasSeenCallout, markCalloutSeen } from '../lib/userPreferences';

/**
 * OnboardingCallout
 *
 * Props:
 *   calloutKey   {string}   Unique key — written to users.dismissed_callouts on dismiss
 *   text         {string}   Callout message
 *   anchorRef    {ref}      React ref attached to the target element (desktop only)
 *   show         {boolean}  External gate — parent controls whether conditions are met
 *                           (e.g. "only show if active tagouts exist")
 *   isMobile     {boolean}  Forces mobile banner mode
 *   onDismiss    {fn}       Optional callback after dismiss
 */
export default function OnboardingCallout({ calloutKey, text, anchorRef, show = true, isMobile: isMobileProp, onDismiss }) {
  const { profile } = useAuth();
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0, placement: 'below' });
  const [mounted, setMounted] = useState(false);
  const calloutRef = useRef(null);

  const isMobile = isMobileProp ?? window.innerWidth <= 768;

  // Decide whether to show on mount — re-checks once profile finishes loading
  useEffect(() => {
    if (!show) return;
    if (hasSeenCallout(profile, calloutKey)) return;
    // Small delay so the page renders before we try to anchor
    const t = setTimeout(() => {
      setVisible(true);
      setMounted(true);
    }, 400);
    return () => clearTimeout(t);
  }, [calloutKey, show, profile]);

  // Position relative to anchor (desktop only)
  //
  // Session 78h. This used to bail out with `!anchorRef?.current` in the guard
  // above and never try again. A ref is mutated rather than set through state,
  // so nothing re-runs this effect when the anchor mounts later — the tooltip
  // would sit at its initial top:0/left:0 in the corner of the window for the
  // life of the page, with no error anywhere. Retries on animation frames until
  // the anchor appears, bounded so a genuinely absent anchor costs about two
  // seconds and then stops rather than spinning forever.
  //
  // Honest note on why this was written, because the reason is NOT the defect it
  // looks like it fixes. 78h saw a tooltip stuck in the corner on the Purchasing
  // Queue and this was the first hypothesis. It was wrong: that anchor was never
  // going to mount, because the ref was threaded into QueueTabs and onto the
  // button but never PASSED at the call site. That is #38's second failure mode
  // exactly — a ref handed over as an anchor and attached to nothing — and it
  // fails silently, which is why it survived a live look at the screen.
  //
  // The retry stays because a late-mounting anchor is a real case on any
  // data-gated screen and this component had no answer for it. But it did not
  // cause the 78h defect and does not detect the one that did. If a callout ever
  // renders in the corner again, check the call site passes the ref BEFORE
  // suspecting timing.
  useEffect(() => {
    if (!visible || isMobile) return;

    let frame = null;
    let attempts = 0;

    function reposition() {
  const anchor = anchorRef?.current;
  if (!anchor) return false;
  const rect = anchor.getBoundingClientRect();
  const calloutEl = calloutRef.current;
  const calloutHeight = calloutEl?.offsetHeight || 80;
  const viewportHeight = window.innerHeight;

  const spaceBelow = viewportHeight - rect.bottom;
  const placement = spaceBelow < calloutHeight + 16 ? 'above' : 'below';

  const top = placement === 'below'
    ? rect.bottom + 8
    : rect.top - calloutHeight - 8;

  const maxLeft = window.innerWidth - 280 - 16;
  const left = Math.min(Math.max(rect.left, 8), maxLeft);

  setPos({ top, left, width: Math.max(rect.width, 260), placement });
  return true;
}

    function tryPosition() {
      if (reposition()) return;
      if (attempts++ > 120) return;
      frame = requestAnimationFrame(tryPosition);
    }

    tryPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [visible, isMobile, anchorRef]);

  function handleDismiss() {
    markCalloutSeen(profile, calloutKey);
    setVisible(false);
    onDismiss?.();
  }

  if (!visible) return null;

  // ── MOBILE: bottom banner ──
  if (isMobile) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: '16px',
          left: '12px',
          right: '12px',
          zIndex: 1200,
          background: '#fff',
          border: '1px solid #D0D5DD',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
          padding: '12px 14px',
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          animation: 'calloutSlideUp 0.22s ease-out',
        }}
      >
        <span style={{ fontSize: '16px', flexShrink: 0, marginTop: '1px' }}>💡</span>
        <span style={{ fontSize: '13px', color: '#1A1A1A', lineHeight: '1.45', flex: 1 }}>{text}</span>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss tip"
          style={{
            flexShrink: 0,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            // #657 (Session 79.22b): #9CA3AF measured 2.54 on white, below the
            // 3.0 a control needs to read as one. --ink-2, 6.69. Size half: #513.
            color: '#5C5C5C',
            lineHeight: 1,
            padding: '0 2px',
            marginTop: '-1px',
          }}
        >×</button>
        <style>{`
          @keyframes calloutSlideUp {
            from { opacity: 0; transform: translateY(10px); }
            to   { opacity: 1; transform: translateY(0); }
          }
        `}</style>
      </div>
    );
  }

  // ── DESKTOP: anchored tooltip ──
  return (
    <div
      ref={calloutRef}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: '268px',
        zIndex: 1200,
        background: '#fff',
        border: '1px solid #D0D5DD',
        borderRadius: '8px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.11)',
        padding: '10px 12px',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-start',
        animation: 'calloutFadeIn 0.18s ease-out',
      }}
    >
      {/* Arrow */}
      <div style={{
        position: 'absolute',
        left: '16px',
        ...(pos.placement === 'below'
          ? { top: '-6px', borderBottom: '6px solid #D0D5DD', borderTop: 'none' }
          : { bottom: '-6px', borderTop: '6px solid #D0D5DD', borderBottom: 'none' }),
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        width: 0,
        height: 0,
      }} />
      {/* Arrow fill (hides border color on inner side) */}
      <div style={{
        position: 'absolute',
        left: '17px',
        ...(pos.placement === 'below'
          ? { top: '-5px', borderBottom: '5px solid #fff', borderTop: 'none' }
          : { bottom: '-5px', borderTop: '5px solid #fff', borderBottom: 'none' }),
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        width: 0,
        height: 0,
      }} />

      <span style={{ fontSize: '14px', flexShrink: 0, marginTop: '1px' }}>💡</span>
      <span style={{ fontSize: '12.5px', color: '#1A1A1A', lineHeight: '1.5', flex: 1 }}>{text}</span>
      <button
        onClick={handleDismiss}
        aria-label="Dismiss tip"
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '17px',
          color: '#5C5C5C',   // #657 - see the phone banner above
          lineHeight: 1,
          padding: '0 2px',
          marginTop: '-1px',
        }}
      >×</button>
      <style>{`
        @keyframes calloutFadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}